import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "gu", label: "ગુજરાતી", short: "GU" },
  { code: "hi", label: "हिन्दी", short: "HI" },
];

/**
 * Reusable language switcher.
 * Two variants:
 *   - "dark"  → for dark landing/login pages (glass pill, white text)
 *   - "light" → for AppShell top bar (subtle grey pill)
 */
export default function LanguageSwitcher({ variant = "light", className = "" }) {
  const { i18n } = useTranslation();
  const active = LANGS.find((l) => l.code === i18n.language) || LANGS.find((l) => i18n.language?.startsWith(l.code)) || LANGS[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem("tilesphere_lang", code); } catch { /* noop */ }
  };

  const triggerCls =
    variant === "dark"
      ? "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white text-xs font-bold uppercase tracking-[0.15em] transition-colors"
      : "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-black/10 bg-white/70 text-foreground/70 hover:text-foreground hover:bg-white text-xs font-bold uppercase tracking-[0.15em] transition-colors";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`${triggerCls} ${className}`}
          data-testid="language-switcher"
          aria-label="Change language"
        >
          <Globe className="w-3.5 h-3.5" />
          <span>{active.short}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => change(l.code)}
            data-testid={`lang-${l.code}`}
            className={`text-sm cursor-pointer ${l.code === active.code ? "font-bold" : ""}`}
          >
            <span className="mr-2 inline-block w-6 text-[10px] font-mono opacity-60">{l.short}</span>
            {l.label}
            {l.code === active.code ? <span className="ml-auto text-orange-500">•</span> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
